<script setup>
import RefundDialog from '@/components/sales/RefundDialog.vue'
import { useApi } from '@/utils/api'
import { useAbility } from '@casl/vue'

definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Sales',
  },
})

const route = useRoute()
const ability = useAbility()
const { formatRupiah, formatDate } = useFormat()

const order = ref(null)
const isLoading = ref(true)
const voidDialog = ref(false)
const refundDialog = ref(false)
const showHpp = ref(false)

// ── Terima Pembayaran (order pending, mis. self-order "Bayar di Kasir") ────
const paymentMethods = [
  { title: 'Cash', value: 'cash' },
  { title: 'QRIS (manual)', value: 'qris' },
  { title: 'Transfer', value: 'transfer' },
  { title: 'Debit', value: 'debit' },
  { title: 'Kredit', value: 'credit' },
]
const confirmMethod = ref('cash')
const confirmReferenceNumber = ref('')
const isConfirmingPayment = ref(false)
const confirmError = ref('')

// ── Payment Transactions (Modul 18 — Midtrans/Xendit) ──────────────────────
// Read-only di sini dengan sengaja: dialog QRIS di POS (PosQrisPaymentDialog)
// SELALU generate charge baru saat dibuka — kalau dipakai ulang di halaman
// ini untuk "lihat QR" transaksi pending yang sudah ada, itu akan membuat
// charge KEDUA untuk order yang sama (Xendit akan menolak dgn DUPLICATE_ERROR
// karena external_id = order_number harus unik). Jadi qr_string yang SUDAH
// ADA dari response GET ditampilkan langsung, tanpa memanggil createCharge lagi.
const paymentTransactions = ref([])
const qrViewDialog = ref(false)
const qrViewTransaction = ref(null)
const qrCanvasRef = ref(null)

const fetchOrder = async () => {
  isLoading.value = true
  try {
    const response = await useApi(`/api/v1/orders/${route.params.id}`).get()
    order.value = response?.data ?? response
  } finally {
    isLoading.value = false
  }
}

const fetchPaymentTransactions = async () => {
  try {
    const response = await useApi(`/api/v1/orders/${route.params.id}/payment-transactions`).get()
    paymentTransactions.value = response?.data ?? response ?? []
  } catch {
    // Diamkan — kalau tenant belum pakai payment gateway sama sekali,
    // biarkan section ini kosong daripada tampilkan error.
    paymentTransactions.value = []
  }
}

onMounted(() => {
  fetchOrder()
  fetchPaymentTransactions()
})

const orderStatusMeta = status => {
  switch (status) {
    case 'completed': return { label: 'Selesai', color: 'success' }
    case 'paid': return { label: 'Sudah Dibayar', color: 'info' }
    case 'void': return { label: 'Void', color: 'error' }
    default: return { label: 'Pending', color: 'warning' }
  }
}

const paymentStatusMeta = status => {
  switch (status) {
    case 'settlement': return { label: 'Berhasil', color: 'success' }
    case 'pending': return { label: 'Menunggu Bayar', color: 'warning' }
    case 'expire': return { label: 'Kedaluwarsa', color: 'error' }
    case 'cancel': return { label: 'Dibatalkan', color: 'error' }
    case 'deny': return { label: 'Ditolak Gateway', color: 'error' }
    default: return { label: 'Gagal', color: 'error' }
  }
}

const openQrView = async transaction => {
  qrViewTransaction.value = transaction
  qrViewDialog.value = true
  await nextTick()
  if (!transaction.qr_string || !qrCanvasRef.value) return
  try {
    const QRCode = await import('qrcode')
    await QRCode.toCanvas(qrCanvasRef.value, transaction.qr_string, { width: 240, margin: 1 })
  } catch (err) {
    console.error('[orders/:id] gagal render QR:', err)
  }
}

const handleVoided = () => {
  fetchOrder()
}

const handleRefunded = () => {
  fetchOrder()
}

const confirmPayment = async () => {
  isConfirmingPayment.value = true
  confirmError.value = ''
  try {
    await useApi(`/api/v1/orders/${route.params.id}/confirm-payment`).post({
      body: {
        method: confirmMethod.value,
        reference_number: confirmMethod.value === 'cash' ? undefined : confirmReferenceNumber.value || undefined,
      },
    })
    confirmReferenceNumber.value = ''
    await fetchOrder()
  } catch (err) {
    confirmError.value = err?.data?.message ?? err?.message ?? 'Gagal mengkonfirmasi pembayaran.'
  } finally {
    isConfirmingPayment.value = false
  }
}

const totalHppOrder = computed(() => {
  if (!order.value?.items) return 0
  return order.value.items.reduce((sum, item) => sum + (item.total_hpp ?? 0), 0)
})

const grossProfit = computed(() => {
  if (!order.value) return 0
  return Number(order.value.subtotal) - totalHppOrder.value
})
</script>

<template>
  <div>
    <VRow class="mb-2">
      <VCol cols="12">
        <VBtn
          variant="text"
          :to="{ name: 'sales-orders' }"
          prepend-icon="ri-arrow-left-line"
          class="mb-2"
        >
          Kembali ke Daftar Order
        </VBtn>
      </VCol>
    </VRow>

    <div v-if="isLoading" class="d-flex justify-center pa-8">
      <VProgressCircular indeterminate color="primary" />
    </div>

    <div v-else-if="!order" class="text-center pa-8">
      <VIcon icon="ri-error-warning-line" size="56" color="grey-lighten-1" />
      <p class="text-body-1 mt-3">Order tidak ditemukan</p>
    </div>

    <template v-else>
      <VRow>
        <!-- ── Kolom Kiri: Info Order + Items ──────────────────────── -->
        <VCol cols="12" md="8">
          <VCard class="mb-4">
            <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3">
              <div>
                <h4 class="text-h5 font-weight-medium">{{ order.order_number }}</h4>
                <p class="text-body-2 text-medium-emphasis mb-0">
                  {{ formatDate(order.created_at, 'DD MMMM YYYY, HH:mm') }}
                  &middot; Kasir: {{ order.creator?.name ?? '-' }}
                </p>
              </div>
              <VChip
                :color="orderStatusMeta(order.status).color"
                size="default"
                variant="tonal"
              >
                {{ orderStatusMeta(order.status).label }}
              </VChip>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="d-flex align-center justify-space-between pa-4">
              <span>Item Pesanan</span>
              <VBtn
                v-if="ability.can('read', 'Inventory')"
                size="small"
                variant="text"
                @click="showHpp = !showHpp"
              >
                {{ showHpp ? 'Sembunyikan HPP' : 'Tampilkan HPP' }}
              </VBtn>
            </VCardTitle>
            <VDivider />

            <VTable>
              <thead>
                <tr>
                  <th>Produk</th>
                  <th class="text-center">Qty</th>
                  <th class="text-end">Harga</th>
                  <th v-if="showHpp" class="text-end">HPP</th>
                  <th class="text-end">Subtotal</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in order.items" :key="item.id">
                  <td>
                    <div class="font-weight-medium">
                      {{ item.product_name_snapshot }}
                      <span v-if="item.variant_name_snapshot && item.variant_name_snapshot.toLowerCase() !== 'default'">
                        ({{ item.variant_name_snapshot }})
                      </span>
                    </div>
                    <div v-if="item.notes" class="text-caption text-medium-emphasis">
                      Catatan: {{ item.notes }}
                    </div>
                  </td>
                  <td class="text-center">{{ item.quantity }}</td>
                  <td class="text-end">{{ formatRupiah(item.unit_price) }}</td>
                  <td v-if="showHpp" class="text-end text-medium-emphasis">
                    {{ formatRupiah(item.total_hpp ?? 0) }}
                  </td>
                  <td class="text-end font-weight-medium">{{ formatRupiah(item.subtotal) }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCard>

          <!-- Void info -->
          <VAlert
            v-if="order.status === 'void'"
            type="error"
            variant="tonal"
          >
            <strong>Order ini telah di-void</strong>
            <div class="text-body-2 mt-1">
              Alasan: {{ order.void_reason }}
            </div>
            <div class="text-caption mt-1">
              Oleh {{ order.voided_by?.name ?? '-' }} pada {{ formatDate(order.voided_at, 'DD MMM YYYY HH:mm') }}
            </div>
          </VAlert>
        </VCol>

        <!-- ── Kolom Kanan: Ringkasan & Pembayaran ─────────────────── -->
        <VCol cols="12" md="4">
          <VCard class="mb-4">
            <VCardTitle class="pa-4">Ringkasan</VCardTitle>
            <VDivider />
            <VCardText>
              <div v-if="order.customer_name" class="d-flex justify-space-between text-body-2 mb-2">
                <span>Pelanggan</span>
                <span class="font-weight-medium">{{ order.customer_name }}</span>
              </div>
              <div class="d-flex justify-space-between text-body-2 mb-2">
                <span>Subtotal</span>
                <span>{{ formatRupiah(order.subtotal) }}</span>
              </div>
              <div
                v-if="order.discount_amount > 0"
                class="d-flex justify-space-between text-body-2 mb-2"
              >
                <span>Diskon</span>
                <span>-{{ formatRupiah(order.discount_amount) }}</span>
              </div>
              <div
                v-if="order.tax_amount > 0"
                class="d-flex justify-space-between text-body-2 mb-2"
              >
                <span>Pajak</span>
                <span>{{ formatRupiah(order.tax_amount) }}</span>
              </div>
              <VDivider class="my-2" />
              <div class="d-flex justify-space-between text-h6 font-weight-bold">
                <span>Total</span>
                <span class="text-primary">{{ formatRupiah(order.total) }}</span>
              </div>

              <template v-if="showHpp">
                <VDivider class="my-3" />
                <div class="d-flex justify-space-between text-body-2 mb-2">
                  <span>Total HPP</span>
                  <span>{{ formatRupiah(totalHppOrder) }}</span>
                </div>
                <div class="d-flex justify-space-between text-body-2 font-weight-medium text-success">
                  <span>Estimasi Laba Kotor</span>
                  <span>{{ formatRupiah(grossProfit) }}</span>
                </div>
              </template>
            </VCardText>
          </VCard>

          <VCard class="mb-4">
            <VCardTitle class="pa-4">Pembayaran</VCardTitle>
            <VDivider />
            <VCardText>
              <div
                v-for="payment in order.payments"
                :key="payment.id"
                class="d-flex justify-space-between text-body-2 mb-2"
              >
                <span class="text-capitalize">{{ payment.payment_method }}</span>
                <span class="font-weight-medium">{{ formatRupiah(payment.amount) }}</span>
              </div>
              <VDivider class="my-2" />
              <div class="d-flex justify-space-between text-body-2 mb-1">
                <span>Total Dibayar</span>
                <span>{{ formatRupiah(order.total_paid ?? 0) }}</span>
              </div>
              <div
                v-if="(order.change_amount ?? 0) > 0"
                class="d-flex justify-space-between text-body-2 text-success"
              >
                <span>Kembalian</span>
                <span>{{ formatRupiah(order.change_amount) }}</span>
              </div>
            </VCardText>
          </VCard>

          <!-- Terima Pembayaran — order pending (mis. self-order "Bayar di Kasir") -->
          <VCard v-if="order.status === 'pending' && ability.can('create', 'Sales')" class="mb-4">
            <VCardTitle class="pa-4">Terima Pembayaran</VCardTitle>
            <VDivider />
            <VCardText>
              <VAlert v-if="confirmError" type="error" variant="tonal" density="compact" class="mb-3" closable @click:close="confirmError = ''">
                {{ confirmError }}
              </VAlert>
              <VSelect
                v-model="confirmMethod"
                :items="paymentMethods"
                label="Metode Pembayaran"
                density="comfortable"
                class="mb-3"
              />
              <VTextField
                v-if="confirmMethod !== 'cash'"
                v-model="confirmReferenceNumber"
                label="No. Referensi (opsional)"
                density="comfortable"
                class="mb-3"
              />
              <VBtn
                block
                color="primary"
                :loading="isConfirmingPayment"
                @click="confirmPayment"
              >
                Konfirmasi Sudah Dibayar — {{ formatRupiah(order.total) }}
              </VBtn>
            </VCardText>
          </VCard>

          <!-- Payment Gateway (QRIS Midtrans/Xendit) — Modul 18 -->
          <VCard v-if="ability.can('read', 'PaymentGateway') && paymentTransactions.length > 0" class="mb-4">
            <VCardTitle class="pa-4">Transaksi Payment Gateway</VCardTitle>
            <VDivider />
            <VList>
              <VListItem
                v-for="tx in paymentTransactions"
                :key="tx.id"
              >
                <VListItemTitle class="d-flex align-center justify-space-between">
                  <span class="text-capitalize">{{ tx.provider }} &middot; {{ tx.payment_type }}</span>
                  <VChip :color="paymentStatusMeta(tx.status).color" size="small" variant="tonal">
                    {{ paymentStatusMeta(tx.status).label }}
                  </VChip>
                </VListItemTitle>
                <VListItemSubtitle class="d-flex align-center justify-space-between mt-1">
                  <span>{{ formatRupiah(tx.gross_amount) }} &middot; {{ formatDate(tx.created_at, 'DD MMM YYYY, HH:mm') }}</span>
                  <VBtn
                    v-if="tx.status === 'pending' && tx.qr_string"
                    size="small"
                    variant="text"
                    @click="openQrView(tx)"
                  >
                    Lihat QR
                  </VBtn>
                </VListItemSubtitle>
              </VListItem>
            </VList>
          </VCard>

          <VBtn
            v-if="order.status === 'completed' && ability.can('create', 'Refund')"
            block
            color="error"
            variant="tonal"
            prepend-icon="ri-refund-2-line"
            class="mb-2"
            @click="refundDialog = true"
          >
            Refund Order Ini
          </VBtn>

          <VBtn
            v-if="order.status === 'completed' && ability.can('delete', 'Sales')"
            block
            color="error"
            variant="tonal"
            prepend-icon="ri-close-circle-line"
            @click="voidDialog = true"
          >
            Void Order Ini
          </VBtn>
        </VCol>
      </VRow>
    </template>

    <VoidOrderDialog
      v-model="voidDialog"
      :order="order"
      @voided="handleVoided"
    />

    <RefundDialog
      v-model="refundDialog"
      :order="order"
      @refunded="handleRefunded"
    />

    <!-- QR view read-only — TIDAK membuat charge baru, cuma render qr_string yang sudah ada -->
    <VDialog v-model="qrViewDialog" max-width="360">
      <VCard>
        <VCardTitle class="pa-4">QRIS Pembayaran</VCardTitle>
        <VDivider />
        <VCardText class="text-center pa-6">
          <canvas ref="qrCanvasRef" class="mx-auto d-block" />
          <p class="text-caption text-medium-emphasis mt-3 mb-0">
            Kedaluwarsa: {{ formatDate(qrViewTransaction?.expired_at, 'DD MMM YYYY, HH:mm') }}
          </p>
        </VCardText>
        <VDivider />
        <VCardActions class="pa-4">
          <VSpacer />
          <VBtn variant="text" @click="qrViewDialog = false">Tutup</VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
